// `spaces` DELETE dispatch for the user-intent field delete of a room
// (#596 / ADR-015 — offline-delete propagation).
//
// This is the PROPAGATING half of the spaces-DELETE branch. The connector
// (connector.ts) only calls it when a tombstone in pending_space_deletes
// matches the deleted row's id — i.e. the DELETE was a surveyor-confirmed
// room delete (capture-client-powersync-delete-space.ts), NOT the re-mint
// orphan-drop (connector-dispatch-space.ts → remintSpaceUnderNewId, which
// leaves no tombstone and is local-acked). It POSTs
// DELETE /capture/sessions/:sessionId/spaces/:spaceId.
//
// CRITICAL — the sessionId comes from the TOMBSTONE, never from the gone row
// or the CRUD entry's opData. A PowerSync DELETE CRUD entry carries ONLY
// entry.id (no opData row, no previousValues unless trackPrevious), and the
// spaces row the id pointed at has already been deleted locally, so neither
// can supply the session. The tombstone (written BEFORE the spaces DELETE)
// is the only durable source of the session id at upload time. The tombstone's
// session id is then re-homed through the in-batch sessionIdRewrites map (same
// as the capture_items POST): if the owning capture_session 409-re-homed
// earlier in THIS batch, the frozen orphan id would 404 — see routeSpaceDelete.
//
// Error handling — none of these arms may throw on an EXPECTED condition or
// the whole upload queue wedges (uploadData acks the batch only once EVERY
// entry succeeds, so one stuck entry blocks the surveyor's CONTRACTUAL count
// uploads behind it). The boundary, mirroring the capture_items / alias PATCH
// contracts:
//   - success (2xx) → the server removed the room. Clear the tombstone +
//     emit `uploaded`.
//   - 404 not_found → the room is already gone server-side (a duplicate
//     confirm, a concurrent delete from another device, or the session/space
//     was never on this contract). The surveyor's intent ("remove this room")
//     is MET either way — this is IDEMPOTENT SUCCESS, not an error. Clear the
//     tombstone + emit `uploaded`. NEVER throw on 404 (see the unwedge note).
//   - failed_precondition → the owning session was completed/cancelled
//     (typically cross-device) before this queued delete uploaded, so the
//     server won't apply it. DROP it (clear the tombstone + emit `dropped`
//     with reason `delete_reverted`) rather than throw: a non-transient
//     failed_precondition would otherwise retry FOREVER and WEDGE the whole
//     queue, blocking contractual count uploads for OTHER buildings. The room
//     stays (the sync stream restores it — correct for a finalised building);
//     the `delete_reverted` reason makes use-delete-room.ts UNDO the optimistic
//     removal so the room reappears (it is NOT lost data), and the surveyor is
//     TOLD the delete didn't apply and can re-delete if it's reopened. Product
//     decision: queue health over delete-preservation on a completed building.
//   - 403 (not owner — should-never-happen) / 429 / 5xx → THROW. The entry AND
//     the tombstone stay in place and the SDK retries the whole batch next
//     cycle (idempotent: a later success/404 acks it) — never silently drop a
//     structural delete on an unexpected error.
//
// Why throwing keeps the tombstone: the throw propagates out of
// dispatchCrudEntry → uploadData's loop bails BEFORE batch.complete(), so the
// spaces-DELETE entry is NOT acked and stays queued. The tombstone is only
// removed on the ack/drop paths (success / 404 / failed_precondition), so on a
// throw (403 / 429 / 5xx) it likewise survives — and the connector re-reads it
// on the retried entry next cycle.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { ApiError, apiFetch } from '../api'

import type { DispatchContext } from './connector-dispatch-context'
import { _emitDropped, _emitUploaded } from './upload-events'

/**
 * Route a spaces-DELETE CRUD entry: propagate it as a user-intent room delete
 * if a tombstone matches, otherwise local-ack it (re-mint orphan-drop / any
 * other local-only DELETE). Called from connector.ts's spaces-DELETE branch —
 * the tombstone lookup + discrimination lives here (next to dispatchSpaceDelete)
 * to keep connector.ts under the line cap and all delete-routing in one file.
 *
 * The discriminator is the pending_space_deletes tombstone: both a user-intent
 * delete and the re-mint orphan-drop emit a byte-identical CRUD entry
 * (op=DELETE, table=spaces, id, NO opData), so the entry alone can't tell them
 * apart. A tombstone (written by capture-client-powersync-delete-space.ts
 * BEFORE its local spaces DELETE) means user-intent → propagate; no tombstone
 * means local-only → ack to drain the queue (the server never accepted that
 * id, or holds it for a DIFFERENT scope we must not touch). A blank session_id
 * is treated as no tombstone so we never build a `…/sessions//spaces/…` URL.
 */
export async function routeSpaceDelete(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  const rows = await db.getAll<{ session_id: string | null }>(
    `SELECT session_id FROM pending_space_deletes WHERE space_id = ? LIMIT 1`,
    [entry.id],
  )
  const tombstoneSessionId = rows[0]?.session_id ?? null
  if (
    typeof tombstoneSessionId === 'string' &&
    tombstoneSessionId.length > 0
  ) {
    // Re-home onto the authoritative session if the owning capture_sessions
    // PUT 409-re-homed earlier in THIS batch (connector-dispatch-session-
    // insert.ts records the orphan→authoritative rewrite). The tombstone froze
    // the orphan session id at delete time; without this remap the DELETE posts
    // against `/capture/sessions/<orphan>/spaces/...`, which the server never
    // had → 404 → dispatchSpaceDelete treats it as idempotent success and
    // clears the tombstone, silently leaving the room on the server even though
    // the surveyor's delete was locally acked (Codex P1 on #631 — most acute on
    // create-then-delete offline, where session + room + delete reconnect in
    // one batch). Mirrors the capture_items POST remap. sessionIdRewrites is
    // populated ONLY for an ACTIVE authoritative session; a completed one is
    // left unmapped → orphan-id DELETE → 404 → idempotent ack, which is correct
    // (a completed building must not be mutated — the same call as the
    // failed_precondition drop below). Cross-BATCH re-homes (the online variant
    // the in-batch map can't reach, see connector-dispatch-create-item.ts) are
    // not recovered here: a stranded delete leaves a surviving room, far lower
    // severity than a lost count, so the count path's building-recovery net is
    // deliberately not mirrored.
    const sessionId =
      context.sessionIdRewrites.get(tombstoneSessionId) ?? tombstoneSessionId
    return dispatchSpaceDelete(entry, db, { sessionId })
  }
  // No tombstone → local-only DELETE (re-mint orphan-drop etc.). Ack.
  return
}

/**
 * Remove the user-intent tombstone for a propagated room delete. Called only
 * on an ack path (server confirmed the delete, or reported it already gone).
 * Keyed by space_id — the tombstone's stable handle (it has no other id the
 * caller knows). A localOnly DELETE generates no upload CRUD entry.
 */
async function clearTombstone(
  db: PowerSyncWriteDb,
  spaceId: string,
): Promise<void> {
  await db.execute(`DELETE FROM pending_space_deletes WHERE space_id = ?`, [
    spaceId,
  ])
}

export async function dispatchSpaceDelete(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: { sessionId: string },
): Promise<void> {
  const { sessionId } = context
  try {
    await apiFetch(
      `/capture/sessions/${encodeURIComponent(
        sessionId,
      )}/spaces/${encodeURIComponent(entry.id)}`,
      { method: 'DELETE' },
    )
    // Server removed the room. Drop the tombstone so a future spaces-DELETE
    // for a re-used id (should-never-happen with UUIDs) isn't mis-propagated,
    // then signal the upload.
    await clearTombstone(db, entry.id)
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Idempotent: the room is already gone server-side (duplicate confirm,
      // concurrent delete, or the session/space isn't on this contract). The
      // surveyor's intent is met — ack like a success. Clearing the tombstone
      // here is what lets the queue drain; NOT throwing is what keeps it from
      // wedging.
      await clearTombstone(db, entry.id)
      _emitUploaded(entry.table, entry.id)
      return
    }
    if (err instanceof ApiError && err.code === 'failed_precondition') {
      // The owning session was completed/cancelled (typically cross-device)
      // before this queued delete uploaded, so the server won't apply it. DROP
      // it rather than throw: a non-transient failed_precondition would retry
      // forever and WEDGE the whole queue, blocking the surveyor's CONTRACTUAL
      // count uploads for OTHER buildings. The room stays (the sync stream
      // restores it — correct for a finalised building). We emit the dedicated
      // `delete_reverted` drop reason (NOT `not_found`): the room and its counts
      // are intact, so use-delete-room.ts UNDOES the optimistic removal (the
      // room reappears) rather than the DroppedChangeToast warning about lost
      // counts — and the surveyor is told the delete didn't apply and can
      // re-delete if the building reopens. The Sentry breadcrumb carries the
      // precise reason.
      Sentry.captureException(err, {
        level: 'warning',
        extra: {
          reason: 'powersync_space_delete_failed_precondition_drop',
          entryId: entry.id,
          sessionId,
        },
      })
      console.warn(
        '[powersync] spaces DELETE failed_precondition (building completed) — ' +
          'dropping the delete to protect the upload queue',
        { id: entry.id, sessionId },
      )
      await clearTombstone(db, entry.id)
      _emitDropped(entry.table, entry.id, 'delete_reverted')
      return
    }
    // 403 (not owner — should-never-happen) / 429 / 5xx: an unexpected or
    // transient error. THROW so the SDK keeps the entry queued (and the
    // tombstone with it) and retries next cycle — never silently drop a
    // structural delete on an unexpected failure.
    throw err
  }
}
